let LxUtils = {}

/**
 * Remove empty keys
 *
 * @param {Object} obj - v-model bzw. datenobjekt welches von leeren keys gesäubert werden soll
 * @returns {{[p: string]: unknown}}
 */
LxUtils.removeEmpty = function (obj) {
  return Object.fromEntries(Object.entries(obj)
    .filter(([_, value]) => {
      if (typeof value === 'object' && value !== null) {
        return Object.entries(value).length > 0
      }
      return value != null
    })
    .map(([key, value]) => [key, value === Object(value) ? LxUtils.removeEmpty(value) : value]) // recursion
  )
}

/**
 * Remove empty keys
 *
 * @param {Object} obj - v-model bzw. datenobjekt welches geändert wurde
 * @param {Object} oldObj - v-model bzw. datenobjekt zum start des Formulars
 */
LxUtils.patchDiff = function (obj, oldObj) {
  return Object.fromEntries(Object.entries(obj)
    .filter(([key, value]) => {
      if (typeof value === 'object') {
        return JSON.stringify(value) !== JSON.stringify(oldObj[key])
      } else {
        return value !== oldObj?.[key]
      }
    })
    .map(([key, value]) => [key, value === Object(value) ? LxUtils.patchDiff(value, oldObj[key]) : (value === undefined || value === null ? '' : value)])
    // recursion + if value === undefined or null change it to empty string for the backend to remove it from db
  )
}

/**
 * This function will accept the two objects as arguments and return the object of deeply merged with nested properties.
 *
 * @param {object} targetObject objects containing the properties to be merged with source.
 * @param {object} sourceObject objects containing the properties you want to apply.
 * @return {object} return the deeply merged objects
 */
LxUtils.deepMergeObject = function (targetObject = {}, sourceObject = {}) {
  // clone the source and target objects to avoid the mutation
  const copyTargetObject = JSON.parse(JSON.stringify(targetObject))
  const copySourceObject = JSON.parse(JSON.stringify(sourceObject))

  // Iterating through all the keys of source object

  if (copySourceObject) {
    Object.keys(copySourceObject).forEach((key) => {
      if (typeof copySourceObject[key] === 'object' && !Array.isArray(copySourceObject[key])) {
        // If property has nested object, call the function recursively
        copyTargetObject[key] = LxUtils.deepMergeObject(copyTargetObject[key], copySourceObject[key])
      } else {
        // else merge the object source to target
        copyTargetObject[key] = copySourceObject[key]
      }
    })
  } else {
    return copySourceObject
  }

  return copyTargetObject
}

/**
 * This function will deeply compare two object merged with nested properties
 *
 * @param {object} object1
 * @param {object} object2
 * @return {boolean} return true if equal and false if different
 */
LxUtils.isDeepEqual = (object1, object2) => {
  const objKeys1 = Object.keys(object1)
  const objKeys2 = Object.keys(object2)

  if (objKeys1.length !== objKeys2.length) {
    return false
  }

  for (let key of objKeys1) {
    const value1 = object1[key]
    const value2 = object2[key]

    const isObjects = LxUtils.isObject(value1) && LxUtils.isObject(value2)

    if (isObjects && Object.prototype.toString.call(value1) === '[object Date]' && Object.prototype.toString.call(value2) === '[object Date]') {
      if (value1.toISOString() !== value2.toISOString()) {
        return false
      }
    }

    // recursion
    if ((isObjects && !LxUtils.isDeepEqual(value1, value2)) || (!isObjects && value1 !== value2)) {
      return false
    }
  }

  return true
}

/**
 * Check value is type object
 *
 * @param object
 * @returns {boolean}
 */
LxUtils.isObject = (object) => {
  return object != null && typeof object === 'object'
}

/**
 * Merge array
 *
 * @param {Array} arr1
 * @param {Array} arr2
 * @param {string} arrayKeyName
 * @returns {Array}
 */
function mergeArrayObjects(arr1, arr2, arrayKeyName) {
  return arr1.map(item => {
    let inner = item
    arr2.forEach(innerItem => {
      if (item[arrayKeyName] === innerItem[arrayKeyName]) {
        inner = innerItem
      }
    })
    return inner
  })
}

/**
 * This function will accept the two objects as arguments and return the object of deeply merged with nested properties.
 *
 * @param {object} targetObject objects containing the properties to be merged with source.
 * @param {object} sourceObject objects containing the properties you want to apply.
 * @param {string} arrayKeyName array key name
 * @return {object} return the deeply merged objects
 */
LxUtils.deepMergeObjectWithSubArray = function (targetObject = {}, sourceObject = {}, arrayKeyName) {
  // clone the source and target objects to avoid the mutation
  const copyTargetObject = JSON.parse(JSON.stringify(targetObject))
  const copySourceObject = JSON.parse(JSON.stringify(sourceObject))

  // Iterating through all the keys of source object
  if (Array.isArray(targetObject) && Array.isArray(sourceObject)) {
    // copyTargetObject[key] = mergeArrayObjects(targetObject,sourceObject,'id')
  } else {
    Object.keys(copySourceObject).forEach((key) => {
      if (typeof copySourceObject[key] === 'object' && !Array.isArray(copySourceObject[key])) {
        // If property has nested object, call the function recursively
        copyTargetObject[key] = LxUtils.deepMergeObjectWithSubArray(copyTargetObject[key], copySourceObject[key], arrayKeyName) // recursion
      } else {
        // else merge the object source to target
        copyTargetObject[key] = copySourceObject[key]
      }
    })
  }

  Object.keys(copySourceObject).forEach((key) => {
    if (typeof copySourceObject[key] === 'object' && !Array.isArray(copySourceObject[key])) {
      // If property has nested object, call the function recursively
      copyTargetObject[key] = LxUtils.deepMergeObjectWithSubArray(copyTargetObject[key], copySourceObject[key], arrayKeyName) // recursion
    } else {
      // else merge the object source to target
      copyTargetObject[key] = copySourceObject[key]
    }
  })

  return copyTargetObject
}

/**
 * Global filters
 *
 * @returns {object}
 */
LxUtils.getGlobalFilters = function () {
  return {
    /**
     * Leading zeros
     *
     * @param {string} value
     * @param {number} size
     * @returns {string}
     */
    leadingZero(value, size = 2) {
      let s = value.toString()
      while (s.length < size) {
        s = '0' + s
      }
      return s
    },

    /**
     * Format date
     *
     * @param {string|Date} value
     * @param {string} _format
     * @returns {null|string}
     */
    formatDate(value, _format = 'DD.MM.YYYY') {
      let format = _format
      if (value) {
        let opts = {
          year: 'numeric', month: '2-digit', day: '2-digit'
        }

        if (format.indexOf('MMMM') >= 0) {
          opts.month = 'long'
        }
        if (format.indexOf('dddd') >= 0) {
          opts = {weekday: 'long'}
        }

        let ev = new Date(String(value))
        return ev.toLocaleDateString('de-DE', opts)
      }
      return null
    },

    /**
     * Format time
     *
     * @param {string|Date} value
     * @param {string} _format
     * @returns {null|string}
     */
    formatTime(value, _format = 'hh:mm:ss') {
      let format = _format
      if (value) {
        let opts = {}
        if (format.indexOf('hh') >= 0) {
          opts.hour = '2-digit'
        }
        if (format.indexOf('mm') >= 0) {
          opts.minute = '2-digit'
        }
        if (format.indexOf('ss') >= 0) {
          opts.second = '2-digit'
        }

        let d = new Date(String(value))
        let ev = d.toLocaleTimeString('de-DE', opts)

        if (ev.length === 1) {
          // fix for single digit value
          ev = '0' + ev
        }
        return ev
      }
      return null
    },

    /**
     * Format decimal
     *
     * @param {Date} value
     * @param {number} numberDigits
     * @returns {*|string}
     */
    formatDecimal(value, numberDigits = 2) {
      if (value) {
        return value.toLocaleString('de-DE', {minimumFractionDigits: numberDigits, maximumFractionDigits: numberDigits})
      }
      return value
    }
  }
}

/**
 * Delete special characters in regex string to provide errors
 *
 * @param {string} stringValue The string for regex-search in backend
 * @returns {string}
 */
LxUtils.clearStringForRegexSearch = function (stringValue) {
  if (typeof stringValue === 'string') {
    const validCharacters = /[^a-z0-9 .,:_öüäÖÜÄß-]/gi
    return stringValue.replace(validCharacters, '') || ''
  }

  return ''
}

/**
 * Fills zeros
 *
 * @param value
 * @param size
 * @returns {string}
 */
LxUtils.fillZeros = function (value, size = 0) {
  value = value.toString()
  while (value.length < size) {
    value = '0' + value
  }

  return value
}

/**
 * Reformat the date to a german format
 *
 * @param date
 * @returns {string}
 */
LxUtils.formatDateFromISODateString = function (date) {
  if (!date) {
    return ''
  }

  // '2019-11-12T11:50:20.849Z'
  let d = new Date(date)

  if (d instanceof Date && !isNaN(d.getDate())) {
    return LxUtils.fillZeros(d.getDate(), 2) + '.' + LxUtils.fillZeros(d.getMonth() + 1, 2) + '.' + d.getFullYear()
  }

  return ''
}

/**
 * Generate uuid v4
 *
 * @returns string
 */
LxUtils.generateUuidV4 = function () {
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c => (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16))
}

/**
 * Check if value is a valid date
 *
 * @param date
 * @returns {boolean}
 */
LxUtils.isValidDate = function (date) {
  if (Object.prototype.toString.call(date) === '[object Date]') {
    return !isNaN(date.getTime())
  }
}

export default LxUtils
